import type { ResumeStorage } from './mount-resume-storage.ts';
import { randomUuid } from './random-uuid.ts';

type Binding = { streamId: string; threadId: string; messageId: string };
type PendingBinding = Binding & { submittedAt: number };
type Options = {
  enabled: boolean;
  threadId: string;
  storage: ResumeStorage;
  resumeApi: (streamId: string) => string;
  recoveryApi: (binding: Binding) => string;
  baseFetch: typeof fetch;
  /** The POST's connection died with no response: whether the server took it is unknown. Fired before the SDK flattens the error to a string. */
  /** Asked before a pending reservation is cancelled on abort; false for an abort that is not the person's Stop. */
  mayStopRun?: (since: number) => boolean;
  begin?: () => number;
  onUnconfirmed?: (binding: Binding) => void;
  attempts?: number;
  delayMs?: number;
};
const PREFIX = 'pending-turn:';
const descriptorOf = (binding: Binding) =>
  PREFIX +
  encodeURIComponent(
    JSON.stringify({
      streamId: binding.streamId,
      threadId: binding.threadId,
      messageId: binding.messageId,
      submittedAt: (binding as PendingBinding).submittedAt,
    }),
  );

function decodePending(value: string): PendingBinding | null {
  if (!value.startsWith(PREFIX) || value.length > 1200) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(value.slice(PREFIX.length)));
    return typeof parsed.submittedAt === 'number' &&
      ['streamId', 'threadId', 'messageId'].every(
        (key) =>
          typeof parsed[key] === 'string' && parsed[key].length > 0 && parsed[key].length <= 128,
      )
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function submittedBinding(init: RequestInit | undefined, threadId: string) {
  if (init?.method !== 'POST' || typeof init.body !== 'string') return null;
  try {
    const body = JSON.parse(init.body);
    const message =
      body.submittedMessageId === undefined
        ? body.messages?.at(-1)
        : { role: 'user', id: body.submittedMessageId };
    if (body.thread_id !== threadId || message?.role !== 'user' || !message.id) return null;
    const binding = {
      streamId: randomUuid(),
      threadId,
      messageId: message.id,
      submittedAt: Date.now(),
    };
    return { binding, body: JSON.stringify({ ...body, recovery: { streamId: binding.streamId } }) };
  } catch {
    return null;
  }
}

function pause(ms: number, signal?: AbortSignal | null) {
  return new Promise<void>((resolve, reject) => {
    signal?.throwIfAborted();
    const done = () => {
      signal?.removeEventListener('abort', abort);
      resolve();
    };
    const timer = setTimeout(done, ms);
    const abort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

// Reuse the SDK's thread-scoped storage and mount-only resume effect. A fresh
// dispatch stores its descriptor before headers; this mount still sees no new
// resume candidate. Reload can GET that exact turn, never repeat its POST.
export function createEarlyTurnRecovery(options: Options) {
  const pendingUrls = new Map<string, PendingBinding>();
  const resumeApi = (streamId: string) => {
    const binding = decodePending(streamId);
    if (!binding) return options.resumeApi(streamId);
    if (!options.enabled || binding.threadId !== options.threadId)
      return options.resumeApi('unavailable');
    const url = options.recoveryApi(binding);
    pendingUrls.set(url, binding);
    return url;
  };

  // Forget THIS original's descriptor only while it is still the stored one: a newer original may have replaced it.
  const forget = (binding: Binding) =>
    options.storage.clearIfCurrent
      ? options.storage.clearIfCurrent(descriptorOf(binding))
      : options.storage.clear();

  async function cancelPending(binding: Binding, since: number, init?: RequestInit) {
    // A detach that is not the person's Stop keeps the original descriptor: the next mount reads it back (GET).
    if (options.mayStopRun) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!options.mayStopRun(since)) return;
    }
    forget(binding);
    try {
      await options.baseFetch(options.recoveryApi(binding), {
        method: 'DELETE',
        headers: init?.headers,
      });
    } catch {
      /* A failed cancel must not mask the user's local Stop. */
    }
  }

  async function recover(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    binding: PendingBinding,
  ) {
    const since = options.begin?.() ?? 0;
    try {
      for (let attempt = 0; attempt < (options.attempts ?? 30); attempt += 1) {
        init?.signal?.throwIfAborted();
        const response = await options.baseFetch(input, init);
        // A reload's GET can outrun POST authentication/reservation. During
        // that first short window, a missing binding is also safe to look up
        // again. It never authorizes resubmitting or running anything.
        const awaitingReservation =
          response.status === 204 &&
          response.headers.get('x-poneva-recovery-terminal') !== '1' &&
          Date.now() - binding.submittedAt < 30_000;
        if (response.status !== 202 && !awaitingReservation) {
          if (response.status === 204 || !response.ok) forget(binding);
          return response;
        }
        await response.body?.cancel();
        await pause(options.delayMs ?? 1000, init?.signal);
      }
      forget(binding);
      return new Response(null, { status: 204 });
    } catch (error) {
      if (init?.signal?.aborted) await cancelPending(binding, since, init);
      throw error;
    }
  }

  const wrappedFetch: typeof fetch = async (input, init) => {
    const url = input instanceof Request ? input.url : String(input);
    const pending = pendingUrls.get(url);
    if (pending && init?.method === 'GET') return recover(input, init, pending);
    const submitted = options.enabled ? submittedBinding(init, options.threadId) : null;
    if (!submitted) return options.baseFetch(input, init);
    const since = options.begin?.() ?? 0;
    init?.signal?.throwIfAborted();
    options.storage.setStreamId(descriptorOf(submitted.binding));
    try {
      const response = await options.baseFetch(input, { ...init, body: submitted.body });
      if (!response.ok || !response.body) forget(submitted.binding);
      return response;
    } catch (error) {
      if (init?.signal?.aborted) await cancelPending(submitted.binding, since, init);
      // fetch rejects with a TypeError when the connection fails; the original is kept for the existing recovery.
      else if (error instanceof TypeError) options.onUnconfirmed?.(submitted.binding);
      throw error;
    }
  };
  return { fetch: wrappedFetch, resumeApi };
}
